import React from 'react';
import { Icon } from './Icon.jsx';
import { LogoMark } from './LogoMark.jsx';
import { AAConsent, loadCalendly } from './CookieBanner.jsx';
import { trackCTAClick, trackScheduleIntent, withUtm } from '../lib/tracking.js';


const { useState } = React;

const CALENDLY_URL = 'https://calendly.com/dominik-gross/infogesprach-app-academy-ws?hide_gdpr_banner=1';

// Click on "Termin buchen" = explicit user action to use the booking widget.
// We treat this as click-wrap consent for Functional (Calendly), Statistics
// (GA4 conversion tracking) and Marketing (Meta SubmitApplication / Lead /
// Contact) — the user is explicitly opting into the booking funnel and
// expects us to track that conversion. Consent is set BEFORE the track*()
// calls so the events fire with active consent in place; otherwise the Meta
// pixel would discard them at the trackPixelEvent consent gate.
async function openCalendlyPopup(location) {
  const c = AAConsent.current();
  if (!c.functional || !c.statistics || !c.marketing) {
    AAConsent.set({ functional: true, statistics: true, marketing: true });
  }
  trackCTAClick({ label: 'Termin buchen', location, destination: 'calendly' });
  trackScheduleIntent({ location });
  const url = withUtm(CALENDLY_URL);
  try {
    await loadCalendly();
    window.Calendly.initPopupWidget({ url });
  } catch {
    window.open(url, '_blank');
  }
}

const openCal = (e) => { e.preventDefault(); openCalendlyPopup('top'); };

// =================== NAV ===================
export function Nav() {
  return (
    <nav className="nav">
      <div className="nav-inner">
        <a href="#top" className="logo">
          <div className="logo-mark"><LogoMark size={32}/></div>
          <span className="logo-text">App Academy</span>
        </a>
        <div className="nav-links">
          <a href="#warum">Warum jetzt</a>
          <a href="#lehrplan">Programm</a>
          <a href="#coach">Team</a>
          <a href="#stimmen">Cases</a>
          <a href="#pakete">Pakete</a>
          <a href="#faq">FAQ</a>
        </div>
        <a href="#" onClick={openCal} className="btn btn-primary btn-sm nav-cta">
          <span className="cta-label-full">Kostenloses Infogespräch</span>
          <span className="cta-label-short">Termin</span>
          <span className="btn-arrow"><Icon.ArrowRight size={14}/></span>
        </a>
      </div>
    </nav>
  );
}

// =================== STICKY CTA ===================
export function StickyCTA() {
  const [visible, setVisible] = useState(false);
  React.useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      const h = window.innerHeight;
      setVisible(y > h * 0.9);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return (
    <div className={`sticky-cta ${visible ? 'visible' : ''}`} aria-hidden={!visible}>
      <div className="sticky-cta-inner">
        <div className="sticky-cta-text">
          <span className="sticky-cta-title cta-label-full">Bereit für deine eigene App?</span>
          <span className="sticky-cta-title cta-label-short">Bereit?</span>
          <span className="sticky-cta-sub">30 Min. online. Unverbindlich. Persönliche Einschätzung.</span>
        </div>
        <a href="#" onClick={openCal} className="btn btn-primary sticky-cta-btn">
          <span className="cta-label-full">Kostenloses Infogespräch</span>
          <span className="cta-label-short">Termin buchen</span>
          <span className="btn-arrow"><Icon.ArrowRight size={14}/></span>
        </a>
      </div>
    </div>
  );
}

// =================== CLIENT STRIP ===================
export function ClientStrip() {
  const apps = [
    { name: 'Gardify', desc: 'Garten-App' },
    { name: 'Impulsschmiede', desc: 'Coaching-App' },
    { name: 'Uniprep', desc: 'Lern-App' },
    { name: 'Draw Buddy', desc: 'Zeichen-App' },
    { name: 'Bring!', desc: 'Shopping-App' },
    { name: '+ 25 weitere', desc: 'live im Store' },
  ];
  return (
    <section className="client-strip">
      <div className="container">
        <div className="client-strip-label">Diese Apps wurden mit unserem Team gebaut</div>
        <div className="client-strip-row">
          {apps.map((a, i) => (
            <div className="client-strip-item" key={i}>
              <div className="client-strip-mark">{a.name.charAt(0)}</div>
              <div className="client-strip-meta">
                <div className="client-strip-name">{a.name}</div>
                <div className="client-strip-desc">{a.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// =================== HERO ===================
export function Hero({ headlineVariant = 'A' }) {
  const [playing, setPlaying] = useState(false);

  const headlines = {
    A: <>Deine App.<br/><em>6 Wochen.</em><br/><span className="accent">Null Code.</span></>,
    B: <>In 6 Wochen<br/>vom <em>leeren Blatt</em><br/>zur App im Store.</>,
    C: <>Andere zahlen <em>100.000 €</em>.<br/><span className="accent">Du baust sie selbst.</span></>,
  };

  return (
    <section className="hero hero-v2" id="top">
      <div className="hero-bg-grid" aria-hidden="true"></div>
      <div className="container">
        <h1 className="hero-headline">{headlines[headlineVariant]}</h1>

        <p className="hero-sub">
          Du hast eine ernstgemeinte App-Idee. <strong>Du baust sie selbst mit KI als Werkzeug</strong> und uns als erfahrenen Sparringspartnern an deiner Seite. In 6 Wochen veröffentlichst du <strong>deine App live im App Store</strong> und bei Google Play. Ohne Programmierkenntnisse. Ohne sechsstelliges Agenturbudget.
        </p>

        <div className="hero-cta-row">
          <a href="#" onClick={openCal} className="btn btn-primary btn-xl">
            <span className="btn-label-stack">
              <span className="btn-main">Kostenloses Infogespräch</span>
              <span className="btn-sub">30 Min. online. Unverbindlich. Persönliche Einschätzung.</span>
            </span>
            <span className="btn-arrow"><Icon.ArrowRight size={18}/></span>
          </a>
          <a href="#vsl" className="btn btn-ghost btn-xl">
            <span className="btn-play"><Icon.Play size={14}/></span>
            <span>Video ansehen. 2 Min.</span>
          </a>
        </div>

        <div className="hero-secondary-cta">
          <a href="#guide" className="hero-secondary-link">
            <Icon.Download size={13}/>
            <span>Oder lade dir den App Academy Guide kostenlos als PDF herunter</span>
            <Icon.ArrowRight size={12}/>
          </a>
        </div>

        <div className="hero-microproof">
          <span className="hero-microproof-item"><Icon.CheckCircle size={14}/> 30+ Apps live im Store</span>
          <span className="hero-microproof-dot"></span>
          <span className="hero-microproof-item"><Icon.Star size={14}/> Ø 4,8 Sterne</span>
        </div>

        <div className="hero-trust-bar">
          <div className="hero-trust-item">
            <span className="hero-trust-num">30+</span>
            <span className="hero-trust-lbl">Apps im Store</span>
          </div>
          <span className="hero-trust-divider"></span>
          <div className="hero-trust-item">
            <span className="hero-trust-num">1 Mio.+</span>
            <span className="hero-trust-lbl">Downloads weltweit</span>
          </div>
          <span className="hero-trust-divider"></span>
          <div className="hero-trust-item">
            <span className="hero-trust-num">4,8 <span className="hero-trust-star">★</span></span>
            <span className="hero-trust-lbl">Ø Bewertung</span>
          </div>
          <span className="hero-trust-divider"></span>
          <div className="hero-trust-item">
            <span className="hero-trust-num">15+</span>
            <span className="hero-trust-lbl">Jahre Erfahrung</span>
          </div>
        </div>
      </div>

      {/* Big VSL showcase below hero */}
      <div className="container vsl-container" id="vsl">
        <div className="vsl-frame" onClick={() => !playing && setPlaying(true)}>
          {playing ? (
            <video controls autoPlay playsInline preload="metadata" src="/assets/VSL-App-Academy.mp4" poster="/assets/dominik-gross.jpg" />
          ) : (
            <>
              <div className="vsl-poster">
                <div className="vsl-poster-bg" aria-hidden="true"></div>
                <div className="vsl-poster-grid" aria-hidden="true"></div>
                <img src="/assets/dominik-gross.jpg" alt="Dominik Groß, Gründer der AI App Academy" width="640" height="800" decoding="async" fetchpriority="high" className="vsl-poster-portrait"/>
                <div className="vsl-poster-vignette" aria-hidden="true"></div>
                <div className="vsl-poster-content">
                  <div className="vsl-poster-eyebrow">
                    <span className="vsl-live"></span>
                    <span>Vorschau · 2:04 Min</span>
                  </div>
                  <div className="vsl-poster-headline">
                    Wie du in 6&nbsp;Wochen<br/>
                    <em>deine eigene App</em> baust.
                  </div>
                  <div className="vsl-poster-byline">
                    <div className="vsl-poster-byline-name">Dominik Groß</div>
                    <div className="vsl-poster-byline-role">Gründer · 15+ Jahre · 1 Mio.+ Downloads</div>
                  </div>
                </div>
              </div>
              <div className="vsl-overlay">
                <button className="vsl-play" aria-label="Video abspielen">
                  <span className="vsl-play-ring vsl-play-ring-1"></span>
                  <span className="vsl-play-ring vsl-play-ring-2"></span>
                  <span className="vsl-play-core">
                    <Icon.Play size={28}/>
                  </span>
                </button>
                <span className="vsl-play-label">Video starten</span>
              </div>
              <div className="vsl-meta-bot">
                <div className="vsl-meta-item">
                  <Icon.Check size={14}/>
                  <span>Was die App Academy wirklich ist</span>
                </div>
                <div className="vsl-meta-item">
                  <Icon.Check size={14}/>
                  <span>Wer geeignet ist. Und wer nicht</span>
                </div>
                <div className="vsl-meta-item">
                  <Icon.Check size={14}/>
                  <span>Wie der Weg in 6 Wochen aussieht</span>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
}

// =================== PROBLEM AGITATION ===================
export function Problem() {
  const points = [
    {
      icon: <Icon.AlertTriangle size={22} />,
      title: 'Die Idee bleibt im Notizbuch.',
      text: 'Vielleicht trägst du sie schon Jahre mit dir herum. Aber jeder ernsthafte Versuch scheitert. Agenturen sind unbezahlbar. Freelancer ghosten. No-Code stößt an Grenzen. Die Idee verstaubt, während andere sie umsetzen.'
    },
    {
      icon: <Icon.DollarSign size={22} />,
      title: 'Agentur-Angebote schockieren.',
      text: '50.000 €. 80.000 €. 120.000 €. Selbst der „günstigste" Anbieter will sechs Monate und mindestens 50k. Und nach dem Launch zahlst du jedes Update extra. Du wirst zum Bittsteller deiner eigenen App.'
    },
    {
      icon: <Icon.Clock size={22} />,
      title: 'Die Welt zieht an dir vorbei.',
      text: '2026 ist eine andere Welt. Mitbewerber launchen Apps in Wochen, nicht in Jahren. Während du auf das nächste Agenturangebot wartest, baut jemand mit weniger Erfahrung, aber den richtigen Tools, bereits deinen Marktanteil ab.'
    },
  ];
  return (
    <section className="section problem-section" id="warum">
      <div className="container">
        <div className="section-head">
          <span className="eyebrow">Warum die meisten App-Ideen sterben</span>
          <h2 className="section-title">Drei Gründe, warum aus <span className="serif-italic">guten Ideen</span> selten Apps werden.</h2>
          <p className="lead">Wenn du dich in einem davon erkennst, ist die App Academy wahrscheinlich für dich gemacht.</p>
        </div>
        <div className="problem-grid">
          {points.map((p,i) => (
            <div className="problem-card" key={i}>
              <div className="problem-icon">{p.icon}</div>
              <h3>{p.title}</h3>
              <p>{p.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// =================== COMPARE ===================
export function Compare() {
  const rows = [
    { label: 'Kosten', old: '50.000 € bis 100.000 €', neu: <>Bruchteil davon<br/><strong>plus Skill fürs Leben</strong></> },
    { label: 'Zeit', old: '3 bis 6 Monate', neu: <><strong>~6 Wochen</strong></> },
    { label: 'Flexibilität', old: 'Jedes Update kostet extra', neu: <>Du passt selbst an, <strong>ohne Wartezeit</strong></> },
    { label: 'Kontrolle', old: 'Extern abhängig', neu: <><strong>Volle Eigenständigkeit</strong></> },
    { label: 'Lernkurve', old: 'Du lernst nichts', neu: <>Du wirst <strong>zum App-Builder</strong></> },
  ];
  return (
    <section className="section">
      <div className="container">
        <div className="section-head">
          <span className="eyebrow">Die Spielregeln haben sich geändert</span>
          <h2 className="section-title">Was eine App <span className="serif-italic">wirklich</span> kostet. Heute.</h2>
          <p className="lead">
            Früher brauchte man ein Entwicklerteam und ein sechsstelliges Budget, um eine App zu bauen.
            2026 reicht eine klare Idee, KI als Werkzeug und sechs Wochen Zeit.
          </p>
        </div>

        <div className="compare-grid">
          {/* Headers */}
          <div className="compare-col compare-old compare-cell compare-cell--header">
            <div className="compare-col-header">
              <span className="compare-col-title">Klassische App-Agentur</span>
              <span style={{fontSize:'11px', color:'var(--text-mute)', fontFamily:'var(--font-mono)'}}>2015 bis 2024</span>
            </div>
          </div>
          <div className="compare-col compare-new compare-cell compare-cell--header">
            <div className="compare-col-header">
              <span className="compare-col-title">Mit KI selbst bauen</span>
              <span style={{fontSize:'11px', color:'var(--accent-2)', fontFamily:'var(--font-mono)'}}>Heute</span>
            </div>
          </div>

          {/* Rows — each pair shares a grid row, so heights match automatically */}
          {rows.map((r,i) => (
            <React.Fragment key={i}>
              <div className="compare-col compare-old compare-cell">
                <div className="compare-row">
                  <span className="compare-row-label">{r.label}</span>
                  <div className="compare-row-value">{r.old}</div>
                </div>
              </div>
              <div className="compare-col compare-new compare-cell">
                <div className="compare-row">
                  <span className="compare-row-label">{r.label}</span>
                  <div className="compare-row-value">{r.neu}</div>
                </div>
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>
    </section>
  );
}

// =================== FOR WHO / NOT WHO ===================
export function ForWho() {
  const yes = [
    'Du hast eine konkrete App-Idee oder ein klares Problem, das eine App lösen könnte',
    'Du bist Gründer:in, Unternehmer:in oder Visionär:in mit Skin in the Game',
    'Du bist bereit, ernsthaft in dich und dein Vorhaben zu investieren. Echte Begleitung von Experten ist für dich keine Hürde, sondern eine Abkürzung',
    'Du bringst 5 bis 10 Stunden pro Woche mit und ein Mindestmaß an digitaler Affinität',
    'Du willst nach 6 Wochen ein echtes Ergebnis im Store. Kein weiteres Zertifikat im Schreibtisch',
  ];
  const no = [
    'Du suchst eine Done-for-you-Agentur, die alles für dich übernimmt',
    'Du willst nur ein bisschen in App-Entwicklung reinschnuppern',
    'Du erwartest, dass eine App über Nacht zum Millionen-Business wird',
    'Du suchst den günstigsten Online-Kurs auf dem Markt. Wir sind kein Discount-Programm',
    'Du bist nicht bereit, 6 Wochen lang konsequent an deinem Projekt zu arbeiten',
  ];
  return (
    <section className="section forwho-section">
      <div className="container">
        <div className="section-head">
          <span className="eyebrow">Ehrlichkeit zuerst</span>
          <h2 className="section-title">Für wen die App Academy <span className="serif-italic">gemacht ist</span>. Und für wen nicht.</h2>
          <p className="lead">Diese Klarheit hilft uns beiden. Im Infogespräch sehen wir gemeinsam in 30 Minuten, ob deine Idee und du zur App Academy passen.</p>
        </div>
        <div className="forwho-grid">
          <div className="forwho-col forwho-yes">
            <div className="forwho-col-header">
              <Icon.Check size={20}/>
              <span>Für dich, wenn …</span>
            </div>
            <ul>
              {yes.map((t,i) => <li key={i}><Icon.Check size={16}/> <span>{t}</span></li>)}
            </ul>
          </div>
          <div className="forwho-col forwho-no">
            <div className="forwho-col-header">
              <Icon.X size={20}/>
              <span>Nicht für dich, wenn …</span>
            </div>
            <ul>
              {no.map((t,i) => <li key={i}><Icon.X size={16}/> <span>{t}</span></li>)}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

// =================== OPPORTUNITY ===================
export function Opportunity() {
  const stats = [
    {
      big: '8,5 Mrd.',
      label: 'Smartphones weltweit.',
      text: 'Jedes einzelne ist dein potenzieller Markt. Du brauchst nicht jeden. Du brauchst nur die richtigen 0,001 %.',
    },
    {
      big: '24 / 7',
      label: 'Apps verkaufen, während du schläfst.',
      text: 'Abos, In-App-Käufe, Werbung. Einnahmen laufen automatisch. In jeder Zeitzone. An jedem Wochentag.',
    },
    {
      big: 'Global',
      label: 'Apple und Google liefern aus.',
      text: 'Hosting, Bezahlung, Distribution. Alles übernehmen die Stores. Du baust einmal. Sie verkaufen weltweit.',
    },
    {
      big: '5-stellig',
      label: 'Monatlicher Umsatz ist normal.',
      text: 'Eine fokussierte Nische, ein paar tausend zahlende Nutzer, ein gutes Abo-Modell. Das ist heute keine Ausnahme.',
    },
  ];

  return (
    <section className="section opportunity-section" id="warum-app">
      <div className="container">
        <div className="section-head">
          <span className="eyebrow">Warum App. Warum jetzt.</span>
          <h2 className="section-title">Eine App ist 2026 das <span className="serif-italic">skalierbarste</span> Business der Welt.</h2>
          <p className="lead">Kein Lager. Kein Versand. Keine Filialen. Du baust ein digitales Produkt einmal und verkaufst es an Millionen Menschen weltweit. Die Stores übernehmen den Rest.</p>
        </div>

        <div className="opportunity-grid">
          {stats.map((s, i) => (
            <div className="opportunity-card" key={i}>
              <div className="opportunity-big">{s.big}</div>
              <div className="opportunity-label">{s.label}</div>
              <p className="opportunity-text">{s.text}</p>
            </div>
          ))}
        </div>

        <div className="opportunity-footnote">
          <Icon.Lock size={14}/>
          <span>Wer heute eine erfolgreiche App im Store hat, baut sich einen digitalen Vermögenswert. Skalierbar. Verkaufbar. Vererbbar.</span>
        </div>
      </div>
    </section>
  );
}

